import {
	LimitedEditionConflictDto,
	LimitedEditionNumberDto,
	OrderProduct,
	ProductSize,
} from '../api-client';
import { sapCodeOfSize } from './quota-utils';

/**
 * Whether a sap code is managed, that is whether it has a pool of numbers on the system.
 * <p>
 * The three states matter and are not the same thing: undefined means the pool has never been loaded, an
 * empty array means the sap code has no number configured and keeps the free text field, and a non empty
 * array means the agent has to pick from the list.
 */
export const hasConfiguredNumbers = (
	numbers?: LimitedEditionNumberDto[],
): boolean => !!numbers && numbers.length > 0;

/**
 * Whether the pool of a sap code has been read from the backend at all.
 * <p>
 * This is the difference that matters most in this feature: an empty pool means the sap code has no number
 * configured and keeps the free text, while a pool never loaded means nothing is known yet. Treating the
 * second as the first lets a row go in the cart with no number on a sap code that does have a pool, and the
 * piece then reaches SAP unnumbered.
 */
export const isPoolLoaded = (numbers?: LimitedEditionNumberDto[]): boolean =>
	numbers !== undefined;

/** How many numbers a size needs: one per piece. */
export const requiredNumbersCount = (quantity?: number | null): number =>
	Math.max(0, quantity ?? 0);

/**
 * Whether the selection of a size is complete, that is one number per piece. A size with no pool is always
 * complete: it is on free text.
 */
export const isSelectionComplete = (
	quantity?: number | null,
	selected?: string[] | null,
	numbers?: LimitedEditionNumberDto[],
): boolean => {
	if (!hasConfiguredNumbers(numbers)) return true;

	return (selected ?? []).length === requiredNumbersCount(quantity);
};

/** Counter under the selector, e.g. "2 of 3 selected". */
export const selectionCountLabel = (
	quantity?: number | null,
	selected?: string[] | null,
): string => `${(selected ?? []).length} of ${requiredNumbersCount(quantity)} selected`;

/** What is missing, to explain why the row cannot go in the cart. Undefined when nothing is missing. */
export const missingNumbersLabel = (
	quantity?: number | null,
	selected?: string[] | null,
	numbers?: LimitedEditionNumberDto[],
): string | undefined => {
	if (isSelectionComplete(quantity, selected, numbers)) return undefined;

	const required = requiredNumbersCount(quantity);
	const chosen = (selected ?? []).length;

	if (chosen > required)
		return `Too many numbers: ${chosen} chosen for ${required} ${
			required === 1 ? 'piece' : 'pieces'
		}.`;

	const missing = required - chosen;

	return `${missing} more ${missing === 1 ? 'number' : 'numbers'} to pick: one per piece.`;
};

/**
 * Keeps the selection within the quantity, dropping what was chosen last.
 * <p>
 * Lowering the quantity has to give the numbers in excess back, otherwise the row would keep holding pieces
 * the agent is no longer ordering.
 */
export const trimSelectionToQuantity = (
	selected?: string[] | null,
	quantity?: number | null,
): string[] => (selected ?? []).slice(0, requiredNumbersCount(quantity));

/**
 * Numbers already picked on the rest of the cart for the same sap code, which must not be offered twice.
 * <p>
 * The reservation refuses the same number twice in one order anyway, but by then the agent has already
 * chosen it: better not to show it at all.
 */
export const numbersTakenElsewhereInCart = (
	products?: OrderProduct[],
	sapCode?: string | null,
	currentRowId?: number | null,
): string[] => {
	if (!sapCode) return [];

	const taken: string[] = [];

	(products ?? []).forEach((product) => {
		if (product.rowId != null && product.rowId === currentRowId) return;

		(product.sizes ?? []).forEach((size) => {
			if (sapCodeOfSize(size) !== sapCode) return;

			(size.limitedEditionCodes ?? []).forEach((code) => {
				if (code && !taken.includes(code)) taken.push(code);
			});
		});
	});

	return taken;
};

/** The numbers a size can offer: the pool minus what the rest of the cart is already holding. */
export const selectableNumbers = (
	numbers?: LimitedEditionNumberDto[],
	takenElsewhere: string[] = [],
): LimitedEditionNumberDto[] =>
	(numbers ?? []).filter((entry) => !takenElsewhere.includes(entry.number));

/**
 * The numbers a whole cart row carries, across its sizes, for the places that only show them: the checkout
 * table and the order summary. Falls back to the free text of the row when no size carries a number, which
 * is the case of a sap code with no pool.
 */
export const rowLimitedEditionNumbers = (product?: OrderProduct): string[] => {
	const codes: string[] = [];

	(product?.sizes ?? []).forEach((size) => {
		(size.limitedEditionCodes ?? []).forEach((code) => {
			if (code && !codes.includes(code)) codes.push(code);
		});
	});

	if (codes.length === 0 && product?.limitedEditionCode)
		codes.push(product.limitedEditionCode);

	return codes;
};

/**
 * The numbers a size ends up with when a second add to cart is merged into an existing row.
 * <p>
 * The cart sums the quantities of two adds of the same product, same facility and same date: the numbers
 * have to be summed with them, or the row would carry two pieces and one number. The union is capped at the
 * new quantity, and an empty union stays undefined so that a sap code on free text is not touched.
 */
export const mergeLimitedEditionCodes = (
	size: ProductSize,
	payload: OrderProduct,
	quantity: number,
): string[] | undefined => {
	const incoming =
		(payload.sizes ?? []).find((entry) => entry.id === size.id)
			?.limitedEditionCodes ?? [];

	const merged: string[] = [];

	[...(size.limitedEditionCodes ?? []), ...incoming].forEach((code) => {
		if (code && !merged.includes(code)) merged.push(code);
	});

	return merged.length > 0 ? merged.slice(0, Math.max(0, quantity)) : undefined;
};

/**
 * Says when a cart row carries a number of pieces that does not match the numbers picked, which is what the
 * order confirmation would refuse. Undefined when the row is consistent, or when it has no number at all
 * and is therefore on free text.
 */
export const rowLimitedEditionMismatch = (
	product?: OrderProduct,
): string | undefined => {
	const offending = (product?.sizes ?? []).find((size) => {
		const codes = (size.limitedEditionCodes ?? []).filter((code) => !!code);

		return codes.length > 0 && codes.length !== (size.quantity ?? 0);
	});

	if (!offending) return undefined;

	const codes = (offending.limitedEditionCodes ?? []).filter((code) => !!code);
	const quantity = offending.quantity ?? 0;

	return `${quantity} ${quantity === 1 ? 'piece' : 'pieces'} but ${codes.length} ${
		codes.length === 1 ? 'number' : 'numbers'
	}: open the row and fix the selection.`;
};

/**
 * Summary of the limited edition problems the backend reports with a 409, both when saving the cart and
 * when confirming the order.
 * <p>
 * A conflict without a number is a row that carries no number at all, so it is worded differently: saying
 * "null: the row carries 2 pieces..." would be noise.
 */
export const limitedEditionErrorsSummary = (
	errors?: LimitedEditionConflictDto[] | null,
): string =>
	(errors ?? [])
		.map((error) =>
			error.number
				? `${error.number}: ${error.reason}`
				: `${error.sapCode ?? 'Product'}: ${error.reason}`,
		)
		.join(' ');

/** Numbers named by the conflicts, to clear them from the selection the agent is looking at. */
export const conflictingLimitedEditionNumbers = (
	errors?: LimitedEditionConflictDto[] | null,
): string[] =>
	(errors ?? [])
		.map((error) => error.number)
		.filter((number): number is string => !!number);
